import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import { dateToString } from "@shared/utils/DateUtils";
import { parseJsAddinfoObject } from "@shared/utils/AddInfoUtils";
import FreeForm from "@widgets/FreeForm";
import { showDialog } from "@widgets/CommonDialog";

interface PopupProps {
  modalRef?: any;
  cd_menu: string;
  id_target: string;
  rowData: any;
}

type AddInfoMeta = {
  cd_flag: string;
  nm_flag?: string;
  tp_data?: string;
  yn_null?: string;
  no_sort?: number;
  dc_qt_valid?: string;
  cd_clas?: string;
};

const FORM_COLSPAN = 3; // 팝업 6컬럼 기준: 한 줄 최대 2개

// 한글 주석: sys_addinfo의 tp_data 값을 SnForm 컨트롤 타입으로 변환한다.
const mapTpDataToCtrl = (tpData?: string) => {
  const tp = String(tpData ?? "").trim().toUpperCase();
  if (tp === "CODE") return "SnComboBox";
  if (tp === "VARCHA") return "SnTextBox";
  if (tp === "NUMERI") return "SnNumberBox";
  if (tp === "DATE") return "SnDateBox";
  if (tp === "BOOL") return "SnCheckBox";
  return "SnTextBox";
};

const SN_SAL_SAO_REG_001_POP_003 = forwardRef(
  ({ modalRef, cd_menu, id_target, rowData }: PopupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const formRef = useRef<any>(null);
    const [controls, setControls] = useState<any[]>([]);
    const [formData, setFormData] = useState<any>({});
    const [dynamicKeys, setDynamicKeys] = useState<string[]>([]);
    const limitRulesRef = useRef<Record<string, { max: number; tp: string }>>({});
    // 한글 주석: SnForm의 최신값 우선, 없으면 로컬 상태값을 사용한다.
    const getCurrentFormData = () => formRef.current?.getFormdata?.() ?? formData;

    // 한글 주석: 동적 키 목록 기준으로 팝업 반환용 values 객체를 구성한다.
    const buildPopupValues = () => {
      const current = getCurrentFormData();
      const values: Record<string, any> = {};
      dynamicKeys.forEach((k) => {
        values[k] = current?.[k] ?? "";
      });
      return values;
    };

    // 한글 주석: 저장(확인) 시점에 오류 필드로 포커스 이동
    const focusInvalidField = (cdParam: string) => {
        const targetIndex = controls.findIndex(
          (c) => String(c?.cd_param ?? "") === cdParam
        );
        if (targetIndex < 0) return;

        const popupRoot: HTMLElement | null =
          modalRef?.current?.getPopupParent?.() ?? null;
        if (!popupRoot) return;

        // 한글 주석: SnForm의 컨트롤 행(.flex.justify-end) 기준으로 대상 행을 찾는다.
        const rows = popupRoot.querySelectorAll<HTMLDivElement>(".flex.justify-end");
        const targetRow = rows?.[targetIndex];
        if (!targetRow) return;

        // 한글 주석: 행 내부 첫 번째 실제 입력 가능한 요소에 포커스
        const target = targetRow.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
        );
        if (target) {
          target.focus();
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };

    useEffect(() => {
      const load = async () => {
        const rtn: any = await fnGetDataSet({
          id: "sys_addinfo_pop_01_01",
          params: {
            cd_firm: userInfo.cd_firm,
            cd_menu,
            id_target,
          },
        });

        const metas: AddInfoMeta[] = (rtn?.[0] ?? [])
          .filter((m: any) => String(m?.cd_flag ?? "").trim() !== "")
          .sort(
            (a: any, b: any) => Number(a?.no_sort ?? 0) - Number(b?.no_sort ?? 0)
          );

        const keys = metas.map((m) => String(m.cd_flag).trim());
        setDynamicKeys(keys);

        // cd_flag별 자릿수 제한 규칙 구성
        const nextRules: Record<string, { max: number; tp: string }> = {};
        metas.forEach((m) => {
          const key = String(m.cd_flag ?? "").trim();
          const max = Number(m.dc_qt_valid ?? 0);
          if (!key || !Number.isFinite(max) || max <= 0) return;
          nextRules[key] = {
            max,
            tp: String(m.tp_data ?? "").trim().toUpperCase(),
          };
        });
        limitRulesRef.current = nextRules;

        const dynamicControls = metas.map((m, idx) => ({
          no_line: String(10000 + idx),
          tp_ctrl: mapTpDataToCtrl(m.tp_data),
          nm_label: String(m.nm_flag ?? m.cd_flag),
          cd_param: String(m.cd_flag),
          cd_clas: String(m.cd_clas ?? ""),
          fg_visible: "Y",
          yn_required: String(m.yn_null ?? "Y") === "N" ? "Y" : "N",
          qt_rowspan: 1,
          qt_colspan: FORM_COLSPAN,
        }));

        const initial: any = {};
        const today = dateToString(new Date());
        const jsAddinfo = parseJsAddinfoObject(rowData?.js_addinfo);
        metas.forEach((m) => {
          const key = String(m.cd_flag ?? "").trim();
          if (!key) return;

          const fromRow = rowData?.[key];
          const fromJsAddinfo = jsAddinfo?.[key];
          const current =
            fromRow !== undefined && fromRow !== null && String(fromRow) !== ""
              ? fromRow
              : fromJsAddinfo;
          const tp = String(m.tp_data ?? "").trim().toUpperCase();
          const isDateType = tp.includes("DATE");
          initial[key] =
            current !== undefined && current !== null && String(current) !== ""
              ? current
              : isDateType
              ? today
              : "";
        });
        setFormData(initial);
        setControls(dynamicControls);
      };

      load();
    }, [cd_menu, id_target, userInfo.cd_firm, rowData]);

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        const current = getCurrentFormData();
        for (const c of controls) {
          if (c.yn_required !== "Y") continue;
          const v = current?.[c.cd_param];
          if (v === undefined || v === null || String(v).trim() === "") {
            await showDialog("alter", `${c.nm_label}은(는) 필수값입니다.`);
            requestAnimationFrame(() => focusInvalidField(String(c.cd_param)));
            return false;
          }
        }

        // 한글 주석: 확인 시점 자릿수 검증 (입력 중 제한 없음)
        for (const c of controls) {
          const key = String(c?.cd_param ?? "");
          const label = String(c?.nm_label ?? key);
          const rule = limitRulesRef.current[key];
          if (!rule || rule.max <= 0 || rule.tp === "BOOL") continue;

          const raw = current?.[key];
          if (raw === undefined || raw === null || raw === "") continue;
          const text = String(raw);
          if (text.length > rule.max) {
            await showDialog(
              "alter",
              `${label}은(는) ${rule.max}자리 이하로 입력해 주세요.`
            );
            requestAnimationFrame(() => focusInvalidField(key));
            return false;
          }
        }
        return true;
      },
      getSelectedValue: async () => {
        return { values: buildPopupValues() };
      },
      // 리사이즈 팝업(CommonModal showResizablePopupModal) 반환 규격
      getOkayData: async () => {
        const values = buildPopupValues();
        return {
          closeFlag: true,
          rtnData: { values },
        };
      },
    }));

    return (
      <div className="h-full">
        <FreeForm
          ref={formRef}
          data={formData}
          controls={controls}
          popupRef={modalRef}
          fitToPopup={true}
          onValueChanged={(_: any, __: string, nextFormData: any) =>
            setFormData(nextFormData)
          }
        />
      </div>
    );
  }
);

export default SN_SAL_SAO_REG_001_POP_003;
